"use client"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Loader2 } from "lucide-react"

import { useComponentsTranslations } from "@/hooks/use-translations"
import { cn } from "@/lib/utils"
import { useMutationBlockerStore } from "@/stores/mutation-blocker-store"

function getMutationCopy(
  kind: "language-change" | "project-create",
  t: ReturnType<typeof useComponentsTranslations>,
) {
  if (kind === "language-change") {
    return {
      title: t("blocking_language_change_title"),
      description: t("blocking_language_change_description"),
    }
  }

  return {
    title: t("blocking_project_create_title"),
    description: t("blocking_project_create_description"),
  }
}

export function MutationBlockingOverlay() {
  const t = useComponentsTranslations()
  const activeMutation = useMutationBlockerStore((state) => state.activeMutation)

  if (!activeMutation) {
    return null
  }

  const copy = getMutationCopy(activeMutation.kind, t)

  return (
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[120] bg-background/80 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby="blocking-mutation-description"
          className={cn(
            "fixed left-1/2 top-1/2 z-[121] w-[calc(100%-2rem)] max-w-sm",
            "-translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl",
            "focus:outline-none",
          )}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
            <div className="space-y-1">
              <DialogPrimitive.Title className="text-base font-semibold">
                {copy.title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description
                id="blocking-mutation-description"
                className="text-sm text-muted-foreground"
              >
                {copy.description}
              </DialogPrimitive.Description>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
